import z from "zod";
import {
  abutmentItemSchema,
  baseItemSchema,
  crownItemSchema,
  implantCrownItemSchema,
  inlayCoreItemSchema,
  waxUpTypeConfigurationSchema,
  isArchScopedItem,
  ItemTypeEnum,
  WaxUpTypeEnum,
  type WaxUpType,
  type ArchScopedItem,
  type Item,
  type ToothScopedItem,
  bridgePonticItemSchema,
  implantBridgeItemSchema,
  type ImplantBridgeItem,
  type BridgePonticItem,
  type AbutmentItem,
  type ImplantCrownItem,
  nightGuardItemSchema,
  bleachingTrayItemSchema,
  retainerItemSchema,
} from "../items";
import {
  ITEM_PREFERENCE_OPTIONS,
  ProductTypeEnum,
  type ImplantBridgeProduct,
  type ImplantCrownProduct,
  type ImplantSplintedCrownProduct,
  type Product,
  type ProductInlayCoreCharacteristics,
  type ProductMaterialCharacteristics,
  type SplintProduct,
  type WaxUpProduct,
} from "./products.types";
import {
  adjacentTeethSchema,
  getArchFromTooth,
  teethSchema,
  uniqueTeethSchema,
} from "../teeth";
import type { CreateMode } from "../types";

const isArchItemInput = (
  item: CreateMode<Item>
): item is CreateMode<ArchScopedItem> => isArchScopedItem(item);

const isToothItemInput = (
  item: CreateMode<Item>
): item is CreateMode<ToothScopedItem> => !isArchItemInput(item);

export const inlayCoreProductSchema = z
  .object({
    teeth: teethSchema,
    type: z.enum(Object.values(ProductTypeEnum)),
    items: z.array(z.union([inlayCoreItemSchema, crownItemSchema])),
  })
  .refine(
    product => {
      const productTeeth = product.teeth;
      const uniqueProductTeeth = new Set(productTeeth);

      return uniqueProductTeeth.size === productTeeth.length;
    },
    {
      message: "Duplicate teeth found in product teeth",
      path: ["teeth"],
    }
  )
  .superRefine((value, ctx) => {
    const uniqueProductTeeth = new Set(value.teeth);

    const inlayCoreItemTeeth = value.items
      .filter(item => item.type === ItemTypeEnum.inlayCore)
      .map(item => item.tooth);
    const allItemKeys = value.items.map(item => `${item.tooth}:${item.type}`);

    value.items.forEach((item, index) => {
      if (
        allItemKeys.filter(key => key === `${item.tooth}:${item.type}`).length >
        1
      ) {
        ctx.addIssue({
          code: "custom",
          message: `Duplicate ${item.type} item found for tooth ${item.tooth}`,
          path: ["items", index, "tooth"],
        });
      }

      if (!uniqueProductTeeth.has(item.tooth)) {
        ctx.addIssue({
          code: "custom",
          message: `Tooth ${item.tooth} in item ${index} is not listed in product teeth`,
          path: ["items", index, "tooth"],
        });
      }
    });

    // Every product tooth requires an inlay-core item; a crown alone is invalid.
    uniqueProductTeeth.forEach(tooth => {
      const isCovered = inlayCoreItemTeeth.includes(tooth);

      if (!isCovered) {
        ctx.addIssue({
          code: "custom",
          message: `Tooth ${tooth} in product teeth is not covered by an inlay-core item`,
          path: ["teeth"],
        });
      }
    });
  }) satisfies z.ZodType<CreateMode<Product>>;

export const getBaseProductSchema = <T extends Item>({
  itemSchema,
}: {
  itemSchema: z.ZodType<CreateMode<T>>;
}) =>
  z
    .object({
      teeth: teethSchema,
      type: z.enum(Object.values(ProductTypeEnum)),
      items: z.array(itemSchema).min(1),
    })
    .refine(
      product => {
        const productTeeth = product.teeth;
        const uniqueProductTeeth = new Set(productTeeth);

        return uniqueProductTeeth.size === productTeeth.length;
      },
      {
        message: "Duplicate teeth found in product teeth",
        path: ["teeth"],
      }
    )
    .superRefine((value, ctx) => {
      const uniqueProductTeeth = new Set(value.teeth);
      const productArches = new Set(
        [...uniqueProductTeeth].map(getArchFromTooth)
      );

      const toothItems = value.items.filter(isToothItemInput);
      const allItemTeeth = toothItems.map(item => item.tooth);

      const itemArches = value.items
        .filter(isArchItemInput)
        .map(item => item.arch);

      if (new Set(itemArches).size !== itemArches.length) {
        ctx.addIssue({
          code: "custom",
          message: "Two items cannot cover the same arch",
          path: ["items"],
        });
      }

      value.items.forEach((item, index) => {
        if (isArchItemInput(item)) {
          if (!productArches.has(item.arch)) {
            ctx.addIssue({
              code: "custom",
              message: `Arch ${item.arch} in item ${index} is not covered by the product teeth`,
              path: ["items", index, "arch"],
            });
          }
          return;
        }

        if (allItemTeeth.filter(t => t === item.tooth).length > 1) {
          ctx.addIssue({
            code: "custom",
            message: `Duplicate tooth ${item.tooth} found across items`,
            path: ["items", index, "tooth"],
          });
        }

        if (!uniqueProductTeeth.has(item.tooth)) {
          ctx.addIssue({
            code: "custom",
            message: `Tooth ${item.tooth} in item ${index} is not listed in product teeth`,
            path: ["items", index, "tooth"],
          });
        }
      });

      /*
      A product tooth is covered either by its own tooth-scoped item, or by an
      arch-scoped item spanning its arch. Both can coexist on the same product: a
      partial denture is scoped to an arch but its clasps sit on individual teeth.
    */
      uniqueProductTeeth.forEach(tooth => {
        const arch = getArchFromTooth(tooth);
        const isCovered =
          allItemTeeth.includes(tooth) || (!!arch && itemArches.includes(arch));

        if (!isCovered) {
          ctx.addIssue({
            code: "custom",
            message: `Tooth ${tooth} in product teeth is not covered by any item`,
            path: ["teeth"],
          });
        }
      });
    }) satisfies z.ZodType<CreateMode<Product>>;

export const waxUpProductSchema = z
  .object({
    teeth: teethSchema,
    type: z.literal(ProductTypeEnum.waxUp),
    items: z.array(waxUpTypeConfigurationSchema),
  })
  .refine(
    product => {
      const productTeeth = product.teeth;
      const uniqueProductTeeth = new Set(productTeeth);

      return uniqueProductTeeth.size === productTeeth.length;
    },
    {
      message: "Duplicate teeth found in product teeth",
      path: ["teeth"],
    }
  )
  .superRefine((value, ctx) => {
    const teethSetByWaxUpType: Record<WaxUpType, Set<number>> = {
      [WaxUpTypeEnum.NUMERICAL]: new Set<number>(),
      [WaxUpTypeEnum.PHYSICAL]: new Set<number>(),
    };

    const itemTeeth = new Set<number>();

    value.items.forEach((item, index) => {
      itemTeeth.add(item.tooth);

      const teethSet = teethSetByWaxUpType[item.waxUpType];

      if (teethSet.has(item.tooth)) {
        ctx.addIssue({
          code: "custom",
          message: `Duplicate tooth ${item.tooth} for wax-up type ${item.waxUpType}`,
          path: ["items", index, "tooth"],
        });
        return;
      }

      teethSet.add(item.tooth);
    });

    const numericalTeethSet = teethSetByWaxUpType[WaxUpTypeEnum.NUMERICAL];
    const physicalTeethSet = teethSetByWaxUpType[WaxUpTypeEnum.PHYSICAL];

    if (numericalTeethSet.size && physicalTeethSet.size) {
      const sameTeeth =
        numericalTeethSet.size === physicalTeethSet.size &&
        [...numericalTeethSet].every(tooth => physicalTeethSet.has(tooth));

      if (!sameTeeth) {
        ctx.addIssue({
          code: "custom",
          message: "Each wax-up type must cover the same teeth",
          path: ["items"],
        });
      }
    }

    if (
      value.teeth.length !== itemTeeth.size ||
      value.teeth.some(tooth => !itemTeeth.has(tooth))
    ) {
      ctx.addIssue({
        code: "custom",
        message: "Product teeth do not match items teeth",
        path: ["teeth"],
      });
    }
  }) satisfies z.ZodType<CreateMode<WaxUpProduct>>;

export const implantCrownSchema = z
  .object({
    teeth: uniqueTeethSchema,
    type: z.literal(ProductTypeEnum.implantCrown),
    items: z
      .array(z.union([implantCrownItemSchema, abutmentItemSchema]))
      .min(2)
      .max(2),
  })
  .refine(
    product => {
      const itemsTeeth = product.items.map(i => i.tooth);
      return itemsTeeth.every(tooth => product.teeth.includes(tooth));
    },
    {
      message: "Some items have teeth that are not listed in the product teeth",
      path: ["items"],
    }
  )
  .refine(
    product => {
      const itemTypes = product.items.map(i => i.type);
      const hasImplantCrown = itemTypes.includes(ItemTypeEnum.implantCrown);
      const hasAbutment =
        itemTypes.includes(ItemTypeEnum.abutment) ||
        itemTypes.includes(ItemTypeEnum.customAbutment);

      return hasImplantCrown && hasAbutment;
    },
    {
      message:
        "Product must contain one implant crown item and one abutment item",
      path: ["items"],
    }
  ) satisfies z.ZodType<CreateMode<ImplantCrownProduct>>;

export const implantSplintedCrownSchema = z
  .object({
    teeth: adjacentTeethSchema,
    type: z.literal(ProductTypeEnum.splintedCrownImplant),
    items: z
      .array(z.union([implantCrownItemSchema, abutmentItemSchema]))
      .min(4),
  })
  .refine(
    product => {
      const itemsTeeth = product.items.map(i => i.tooth);
      return itemsTeeth.every(tooth => product.teeth.includes(tooth));
    },
    {
      message: "Some items have teeth that are not listed in the product teeth",
      path: ["items"],
    }
  )
  .refine(
    product => {
      const itemsByTooth = product.items.reduce<
        Record<number, Array<CreateMode<ImplantCrownItem | AbutmentItem>>>
      >((acc, item) => {
        if (!acc[item.tooth]) {
          acc[item.tooth] = [];
        }
        acc[item.tooth].push(item);
        return acc;
      }, {});

      return product.teeth.every(tooth => {
        const itemTypes = (itemsByTooth[tooth] ?? []).map(item => item.type);

        return (
          itemTypes.length === 2 &&
          itemTypes.includes(ItemTypeEnum.implantCrown) &&
          (itemTypes.includes(ItemTypeEnum.abutment) ||
            itemTypes.includes(ItemTypeEnum.customAbutment))
        );
      });
    },
    {
      message: "Each tooth must contain one implant crown and one abutment",
      path: ["items"],
    }
  ) satisfies z.ZodType<CreateMode<ImplantSplintedCrownProduct>>;

export const implantBridgeSchema = z
  .object({
    teeth: adjacentTeethSchema,
    type: z.literal(ProductTypeEnum.bridgeImplant),
    items: z
      .array(
        z.union([
          implantBridgeItemSchema,
          bridgePonticItemSchema,
          abutmentItemSchema,
        ])
      )
      .min(2),
  })
  .refine(
    product => {
      const itemsTeeth = product.items.map(i => i.tooth);
      return itemsTeeth.every(tooth => product.teeth.includes(tooth));
    },
    {
      message: "Some items have teeth that are not listed in the product teeth",
      path: ["items"],
    }
  )
  .refine(
    product => {
      const itemsByTooth = product.items.reduce<
        Record<
          number,
          Array<CreateMode<ImplantBridgeItem | BridgePonticItem | AbutmentItem>>
        >
      >((acc, item) => {
        if (!acc[item.tooth]) {
          acc[item.tooth] = [];
        }
        acc[item.tooth].push(item);
        return acc;
      }, {});

      return product.teeth.every(tooth => {
        const items = itemsByTooth[tooth];
        if (!items?.length) {
          return false;
        }

        const itemTypes = items.map(item => item.type);

        const isPonticTooth =
          items.length === 1 && itemTypes.includes(ItemTypeEnum.bridgePontic);
        const isImplantTooth =
          items.length === 2 &&
          itemTypes.includes(ItemTypeEnum.bridgeImplant) &&
          (itemTypes.includes(ItemTypeEnum.abutment) ||
            itemTypes.includes(ItemTypeEnum.customAbutment));

        return isPonticTooth || isImplantTooth;
      });
    },
    {
      message:
        "Each tooth must contain one pontic or one bridge implant and one abutment",
      path: ["items"],
    }
  ) satisfies z.ZodType<CreateMode<ImplantBridgeProduct>>;

export const productMaterialCharacteristicsSchema = z.object({
  items: z.array(
    baseItemSchema.pick({ tooth: true, materialCharacteristics: true })
  ),
}) satisfies z.ZodType<ProductMaterialCharacteristics>;

export const productInlayCoreCharacteristicsSchema = z.object({
  items: z.array(
    inlayCoreItemSchema.pick({
      tooth: true,
      materialCharacteristics: true,
      inlayCoreCharacteristics: true,
    })
  ),
}) satisfies z.ZodType<ProductInlayCoreCharacteristics>;

export const productPreferencesSchema = z.object({
  itemPreferences: z
    .object({
      occlusalContactTightness: z
        .enum(ITEM_PREFERENCE_OPTIONS.occlusalContactTightness)
        .nullish(),
      proximalContactTightness: z
        .enum(ITEM_PREFERENCE_OPTIONS.proximalContactTightness)
        .nullish(),
      noOcclusalClearance: z
        .enum(ITEM_PREFERENCE_OPTIONS.noOcclusalClearance)
        .nullish(),
      occlusalStaining: z
        .enum(ITEM_PREFERENCE_OPTIONS.occlusalStaining)
        .nullish(),
      interproximalContactDesign: z
        .enum(ITEM_PREFERENCE_OPTIONS.interproximalContactDesign)
        .nullish(),
      anteriorPosteriorPonticDesign: z
        .enum(ITEM_PREFERENCE_OPTIONS.anteriorPosteriorPonticDesign)
        .nullish(),
    })
    .refine(preferences => Object.values(preferences).some(Boolean), {
      message: "At least one preference must be set",
    }),
}) satisfies z.ZodType<Pick<Product, "itemPreferences">>;

export const splintSchema = getBaseProductSchema<
  SplintProduct["items"][number]
>({
  itemSchema: z.union([
    nightGuardItemSchema,
    bleachingTrayItemSchema,
    retainerItemSchema,
  ]),
}).safeExtend({
  type: z.literal(ProductTypeEnum.splint),
}) satisfies z.ZodType<CreateMode<SplintProduct>>;
